import {
  dashboardExact as exact,
  dashboardRecord as record,
  dashboardRegisterId as registerId,
  dashboardString as string,
  dashboardValidationFail as fail,
  type DashboardUnknownRecord as UnknownRecord,
} from './FmDashboardOverview.validationHelpers';

export const CHIP_VARIANTS = new Set(['brand', 'info', 'warn', 'success', 'mute']);
export const COLUMN_ROLES = new Set([
  'label',
  'numeric',
  'money',
  'delta',
  'status',
  'source',
  'metadata',
]);
export const MEDIA_ENUMS = {
  scope: new Set(['editorial', 'property', 'stand']),
  species: new Set(['pine', 'spruce', 'birch', 'deciduous', 'mixed', 'other', 'unknown']),
  developmentClass: new Set(['A0', 'T1', 'T2', 'unknown']),
  landClass: new Set([
    'productive',
    'low-productive',
    'nonproductive-peatland',
    'other',
    'unknown',
  ]),
  structure: new Set([
    'pine-overstory-spruce-regeneration',
    'birch-overstory-spruce-regeneration',
    'other',
    'unknown',
  ]),
  condition: new Set(['conifer-mortality', 'broken-trees', 'other', 'unknown']),
  reportedCause: new Set([
    'insect-or-parasite',
    'pathogen',
    'wind',
    'snow',
    'fire',
    'drought',
    'machinery',
    'other',
    'unknown',
  ]),
} as const;

export const RANKED_RECORD_KEYS = [
  'id',
  'entityId',
  'propertyId',
  'rank',
  'title',
  'stateSentence',
  'reason',
  'status',
  'mediaContext',
  'mediaCueLabels',
  'propertyMap',
  'summaryFields',
  'expertValues',
  'evidence',
  'primaryAction',
  'detailsAction',
  'secondaryAction',
  'removeAction',
  'assistantAction',
] as const;
export const RANKED_EVIDENCE_RECORD_KEYS = [
  ...RANKED_RECORD_KEYS.slice(0, 7),
  'changeSummary',
  'changeIds',
  ...RANKED_RECORD_KEYS.slice(7),
] as const;
export const RANKED_SECTION_KEYS = [
  'kind',
  'id',
  'showRank',
  'ownerQuestion',
  'literalAnswer',
  'description',
  'ariaLabel',
  'state',
  'rankingRunId',
  'topRecordId',
  'recordKind',
  'records',
  'expertTable',
  'recipe',
  'evidence',
] as const;

export function strings(value: UnknownRecord, path: string, fields: readonly string[]): void {
  fields.forEach((field) => string(value[field], `${path}.${field}`));
}

export function optionalStrings(
  value: UnknownRecord,
  path: string,
  fields: readonly string[],
): void {
  fields.forEach((field) => {
    if (value[field] !== undefined) string(value[field], `${path}.${field}`);
  });
}

export function array(value: unknown, path: string, minimum = 1): unknown[] {
  if (!Array.isArray(value) || value.length < minimum) {
    fail(path, `must contain at least ${minimum} item${minimum === 1 ? '' : 's'}`);
  }
  return value;
}

export function scalar(value: unknown, path: string): void {
  if (value === null || typeof value === 'string' || typeof value === 'boolean') return;
  if (typeof value === 'number' && Number.isFinite(value)) return;
  fail(path, 'must be a string, finite number, boolean, or null');
}

export function validateAction(
  value: unknown,
  path: string,
  ids: Set<string>,
  options: { allowIntent: boolean; allowIcon?: boolean } = { allowIntent: true },
): void {
  const action = record(value, path);
  const keys = options.allowIntent ? ['id', 'label', 'jobId', 'intent'] : ['id', 'label', 'jobId'];
  if (options.allowIcon) keys.push('icon');
  keys.push('pending');
  exact(action, keys, path);
  if (action.pending !== undefined && typeof action.pending !== 'boolean') {
    fail(`${path}.pending`, 'must be a boolean');
  }
  registerId(action.id, `${path}.id`, ids);
  strings(action, path, ['label', 'jobId']);
  if (
    options.allowIntent &&
    action.intent !== undefined &&
    !['primary', 'secondary'].includes(String(action.intent))
  ) {
    fail(`${path}.intent`, 'must be primary or secondary');
  }
}

export function validateRemoveAction(value: unknown, path: string, ids: Set<string>): void {
  const action = record(value, path);
  exact(action, ['id', 'label', 'jobId', 'confirmation', 'pending', 'error'], path);
  registerId(action.id, `${path}.id`, ids);
  strings(action, path, ['label', 'jobId']);
  if (typeof action.pending !== 'boolean') fail(`${path}.pending`, 'must be a boolean');
  if (action.error !== null) string(action.error, `${path}.error`);
  const confirmationPath = `${path}.confirmation`;
  const confirmation = record(action.confirmation, confirmationPath);
  exact(
    confirmation,
    ['title', 'description', 'confirmLabel', 'cancelLabel', 'affectedEntities'],
    confirmationPath,
  );
  strings(confirmation, confirmationPath, ['title', 'description', 'confirmLabel', 'cancelLabel']);
  array(confirmation.affectedEntities, `${confirmationPath}.affectedEntities`).forEach(
    (entry, index) => {
      const entityPath = `${confirmationPath}.affectedEntities[${index}]`;
      const entity = record(entry, entityPath);
      exact(entity, ['id', 'label', 'outcome'], entityPath);
      strings(entity, entityPath, ['id', 'label', 'outcome']);
    },
  );
}
